import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AppDispatch, RootState } from '../redux-stores/store';
import { useDispatch } from 'react-redux';
import { fetchConversionCampaignThunk } from '../redux-stores/slices/conversionCampaignSlice';
import { useSelector } from 'react-redux';

import AddNewConversionCampaignLink from '../components/conversionCampaign/AddNewConversionCampaignLink';
import NotFound from './system/NotFound';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from "swiper/modules";
import 'swiper/css';
import 'swiper/css/pagination';
import conversionCampaignInstructionImg from "../assets/images/conversion-campaign-instructions.png"
import ConversionCampaignLinks from '../components/conversionCampaign/ConversionCampaignLinks';
import usePageTitle from '../hooks/usePageTitle';
import { setHeader, setShowFooterNav } from '../redux-stores/slices/layoutSlice';
import Skeleton from 'react-loading-skeleton';
import { defaultAppHeaderProps } from '../types/layout/layoutTypes';
const ConversionCampaign: React.FC = () => {

    const { campaignId } = useParams();
    const dispatch: AppDispatch = useDispatch();
    const [showAddLink, setShowAddLink] = useState(true);
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('details');
    const [addLink, setAddLink] = useState(false);

    const campaign = useSelector((state: RootState) => state.conversionCampaigns.items[campaignId!]?.campaign);

    usePageTitle(campaign?.name ?? '')

    const loading = useSelector((state: RootState) => state.conversionCampaigns.itemLoading);

    useEffect(() => {
        if (campaignId) {
            dispatch(fetchConversionCampaignThunk({ campaignId }))
        }
    }, [campaignId])

    const reset = () => {
        setShowAddLink(false);
        setTimeout(() => {
            setShowAddLink(true)
        }, 200)
    }

    useEffect(() => {
        dispatch(setShowFooterNav(false));
        return () => {
            dispatch(setShowFooterNav(true));
        }
    }, [dispatch])

    useEffect(() => {
        if(campaign) {
            dispatch(setHeader({
                generic: false,
                page: {
                    title: campaign.name,
                    onBackClick: () => navigate(`/conversion`)
                }
            }))
        }

        return () => {
            dispatch(setHeader(defaultAppHeaderProps))
        }
    }, [campaign])

    if (!campaign && !loading) return <NotFound />;

    return (
        <React.Fragment>
            <div className="page-wrapper app-page">

                <div className="conversion-campaign-page mb-4">
                    {
                        loading ? (
                            <div className="conversionCampaignSlider p-4">
                                <Skeleton height={250} />
                            </div>

                        ) : (
                            <Swiper pagination={true} autoplay={true} loop={true} modules={[Pagination, Autoplay]} className="conversionCampaignSlider">
                                {
                                    [campaign?.img_url, conversionCampaignInstructionImg].map((img, i) => {
                                        return (
                                            <SwiperSlide className='p-4' key={i}>
                                                <div className='conversion-campaign-slider-img bg-image bgc-white' style={{ backgroundImage: `url(${img})` }}></div>
                                            </SwiperSlide>
                                        )
                                    })
                                }
                            </Swiper>
                        )
                    }

                    <div className="conversion-campaign-page-brand-section">
                        {
                            loading ? (
                                <Skeleton height={30} />
                            ) : (
                                <>
                                    <div className="d-flex gap-1 align-items-center">
                                        <span className="conversion-campaign-page-brand-name">{campaign?.name}</span>
                                        {
                                            campaign?.url &&
                                            <a href={campaign.url} target='_blank' rel='noreferrer'>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                                    <circle cx="12" cy="12.5" r="10" stroke="var(--primary)" strokeWidth="1.5" />
                                                    <path d="M9 15.5L15 9.5M15 9.5H10.5M15 9.5V14" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </a>
                                        }
                                    </div>
                                    {/* <span className="conversion-campaign-page-brand-category">{campaign?.category}</span> */}
                                </>
                            )
                        }

                    </div>
                    <div className='border-bottom-1'>
                        <div className="conversion-campaign-tab-controls px-3">
                            <span className={`conversion-campaign-tab-button ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>Campaign Details</span>
                            <span className={`conversion-campaign-tab-button ${activeTab === 'links' ? 'active' : ''}`} onClick={() => setActiveTab('links')}>My Links</span>
                        </div>
                    </div>
                    <div data-bs-parent="#tab-group-3" className={`collapse ${activeTab === 'details' ? 'show' : ''} p-3`} id="tab-8">
                        {
                            campaign?.description &&
                            <div className="mb-3">
                                {
                                    loading ? (
                                        <>
                                            <Skeleton height={10} />
                                            <Skeleton height={10} />
                                            <Skeleton height={10} />
                                        </>
                                    ) : (
                                        <p className="campaign-details">{campaign?.description}</p>
                                    )
                                }
                            </div>
                        }
                        <div className="earning-potentials-body mb-3">
                            <div className="d-flex justify-content-between w-100">
                                <span className="earning-potential-table-title w-25 text-start">Tracking</span>
                                {
                                    loading ? (
                                        <Skeleton height={10} width={80} />
                                    ) : (
                                        <span className="earning-potential-table-value text-end">{campaign?.reporting_type === 'realtime' ? 'Real Time' : (campaign?.reporting_period ? `${campaign?.reporting_period} Days` : 'Offline')}</span>
                                    )
                                }
                            </div>
                            {
                                campaign?.validation_period &&
                                <div className="d-flex justify-content-between w-100">
                                    <span className="earning-potential-table-title w-25 text-start">Confirmation</span>
                                    {
                                        loading ? (
                                            <Skeleton height={10} width={80} />
                                        ) : (
                                            <span className="earning-potential-table-value text-end">{campaign?.validation_period} Days</span>
                                        )
                                    }
                                </div>
                            }
                            {
                                campaign?.payment_period &&
                                <div className="d-flex justify-content-between w-100">
                                    <span className="earning-potential-table-title w-25 text-start">Payment</span>
                                    {
                                        loading ? (
                                            <Skeleton height={10} width={80} />
                                        ) : (
                                            <span className="earning-potential-table-value w-50 text-end">{campaign?.payment_period} Days <span className='after-confirmation'>(after confirmation)</span></span>
                                        )
                                    }
                                </div>
                            }

                        </div>
                        {
                            (campaign?.payout_categories?.length ?? 0) > 0 &&
                            <div className="earning-potentials">
                                <div className="earning-potentials-header">
                                    <div className="earning-potentials-title">
                                        <span className="earning-potentials-title-text text-dark">Earning Potential</span>
                                    </div>
                                </div>

                                <div className="earning-potentials-body p-0">
                                    <div className="d-flex justify-content-between w-100 p-3 ps-2 pb-3">
                                        <span className="earning-potential-table-value w-100 fw-bold ps-2" style={{borderLeft: '3px solid #007AAF', fontSize: '16px'}}>Category of product</span>
                                        {/* <span className="earning-potential-table-title">Earning</span> */}
                                    </div>
                                    <div className="separator"></div>
                                    {
                                        campaign?.payout_categories?.map((category, i) => {
                                            return (
                                                <div className={`d-flex justify-content-between w-100 border-bottom p-3 ${category.is_header ? 'earning-category-header' : ''}`} key={i}>
                                                    <span className={`earning-potential-table-value w-75 text-start ${category.is_header ? 'fw-semibold w-100' : ''}`}>{category.name}</span>
                                                   {!category.is_header && <span className={`earning-potential-table-value text-end ${category.is_header ? 'fw-semibold text-success' : ''}`}>{category.type === 'fixed' ? `₹${category.payout}` : `${category.payout}%`}</span>}
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                        }

                        {
                            (campaign?.terms?.length ?? 0) > 0 &&
                            <div className='mt-3'>
                                <span className="earning-potentials-title-text pop">Terms & Conditions</span>
                                <ol className="terms px-3 mt-2">
                                    {
                                        campaign?.terms?.map((term, i) => {
                                            return (
                                                <li className='earning-potential-table-value w-100 mb-2' key={i}>{term}</li>
                                            )
                                        })
                                    }
                                </ol>
                            </div>
                        }
                    </div>

                    <div data-bs-parent="#tab-group-3" className={`collapse ${activeTab === 'links' ? 'show' : ''}`} id="tab-8">
                        <ConversionCampaignLinks />
                    </div>

                </div>

            </div>
            <div className="sticky-footer">
                <button onClick={() => setAddLink(true)} className='btn btn-primary w-100'>Create Earning Link</button>
            </div>
            {
                (campaignId && showAddLink) &&
                <AddNewConversionCampaignLink
                    isActive={addLink}
                    onClose={() => {
                        setAddLink(false);
                        reset()
                    }}
                    campaignId={campaignId}
                />
            }
        </React.Fragment>
    )
}

export default ConversionCampaign